/**
 * pdf-fonts.js — Unicode font loader for jsPDF.
 *
 * jsPDF's built-in helvetica/courier fonts are PDF Type 1 with Latin-1 only.
 * Calling doc.text('Аудио', ...) renders garbage glyphs because the font has
 * no codepoints for Cyrillic/Korean/Arabic/etc. — the symptom users report
 * as "буквы изменены на что-то другое" / "looks like missing Unicode".
 *
 * This helper lazy-loads a Noto Sans TTF that covers the user's current
 * locale, registers it with jsPDF via doc.addFileToVFS + doc.addFont, and
 * sets it as the default. Tools then call doc.setFont('NotoSans', weight)
 * (or just keep using setFont('helvetica', ...) — we alias the calls).
 *
 * Usage in a tool:
 *
 *   await window.__pdfLoadUnicodeFont(doc);
 *   doc.setFont('NotoSans', 'bold');
 *   doc.text('Аудиоинспектор', x, y);
 *
 * The helper is idempotent — calling it twice on the same doc is a no-op.
 * It picks a locale-appropriate TTF (KR/JP/SC for CJK, Arabic for ar,
 * NotoSans Regular+Bold otherwise). For mixed-script content a tool can
 * pass an explicit `script` arg to override:
 *   await __pdfLoadUnicodeFont(doc, 'cyrillic');
 *
 * Font files live in /vendor/fonts/ and are downloaded via the existing
 * download.sh script. Sizes (Regular+Bold pair, where applicable):
 *   NotoSans (latin+cyrillic+greek)  — 570KB + 576KB = 1.1MB
 *   NotoSansArabic-Bold              — 297KB (single weight)
 *   NotoSansKR-Bold                  — 2.4MB (single weight; CJK is heavy)
 *   NotoSansJP-Bold                  — 2.4MB
 *   NotoSansSC-Bold                  — 2.5MB
 * Loaded only when a tool calls __pdfLoadUnicodeFont — page initial load
 * is unaffected.
 */
(function(){
'use strict';
if (window.__pdfLoadUnicodeFont && !window.__pdfLoadUnicodeFont.__isStub) return;

// Locale → font set. CJK + Arabic are single-weight (Bold rendering for both
// regular and bold contexts) because the per-script TTF is large and
// shipping two per locale doubles the download for users that may never
// hit Export PDF. Latin+Cyrillic gets the proper Regular/Bold pair since
// each file is small and weight differences matter for most layouts.
// Each script ships a Regular + Bold pair so doc.setFont(family, 'bold')
// resolves to a real bold file (not just regular re-tagged), preserving
// proper visual weight contrast in headers vs body text.
var FONT_SETS = {
    cyrillic: {
        family: 'NotoSans',
        files: { normal: '/vendor/fonts/NotoSans-Regular.ttf', bold: '/vendor/fonts/NotoSans-Bold.ttf' }
    },
    latin: {
        family: 'NotoSans',
        files: { normal: '/vendor/fonts/NotoSans-Regular.ttf', bold: '/vendor/fonts/NotoSans-Bold.ttf' }
    },
    korean: {
        family: 'NotoSansKR',
        files: { normal: '/vendor/fonts/NotoSansKR-Regular.ttf', bold: '/vendor/fonts/NotoSansKR-Bold.ttf' }
    },
    japanese: {
        family: 'NotoSansJP',
        files: { normal: '/vendor/fonts/NotoSansJP-Regular.ttf', bold: '/vendor/fonts/NotoSansJP-Bold.ttf' }
    },
    chinese: {
        family: 'NotoSansSC',
        files: { normal: '/vendor/fonts/NotoSansSC-Regular.ttf', bold: '/vendor/fonts/NotoSansSC-Bold.ttf' }
    },
    arabic: {
        /*
         * IBM Plex Sans Arabic, а НЕ Noto Sans Arabic — и это исправление
         * живого дефекта, а не вкусовщина.
         *
         * Noto Sans Arabic покрывает ТОЛЬКО арабицу: 1223 кодовых точки, ни
         * одной цифры, ни точки, ни латинской буквы (замерено по cmap
         * 2026-08-08). А jsPDF при отсутствующем глифе не рисует коробку — он
         * МОЛЧА выбрасывает весь фрагмент текста. Замерено рендером, с
         * латинским контролем в отдельном документе:
         *
         *   «شهادة إنشاء»   (чистый арабский)      отрисовалось
         *   «التاريخ 2026»  (арабский + цифры)     ИСЧЕЗЛО целиком
         *   «2026-08-08»    (только цифры)         ИСЧЕЗЛО
         *   «التاريخ: نص»   (арабский + двоеточие) осталось только «نص»
         *
         * То есть в арабском /creation-certificate пропадали дата и хеш, а в
         * арабском /resume-builder — телефон, почта, даты и стаж. Документ
         * выглядел выданным и был выпотрошен, причём без единой ошибки в
         * консоли: молчаливый пропуск хуже тофу, потому что о нём нечему
         * сигналить.
         *
         * IBM Plex Sans Arabic (OFL) несёт арабицу и латиницу с цифрами в
         * одном файле, поэтому строка остаётся ОДНИМ шрифтовым прогоном.
         * Разбивать её по скриптам — не выход: это рисует глифы и ломает
         * двунаправленный порядок (скобки и двоеточия уезжают не на ту
         * сторону). Сторож — tests/Feature/PdfFontCoverageTest.php.
         */
        family: 'IBMPlexSansArabic',
        files: { normal: '/vendor/fonts/IBMPlexSansArabic-Regular.ttf', bold: '/vendor/fonts/IBMPlexSansArabic-Bold.ttf' }
    }
};

function localeToScript(locale){
    locale = (locale || '').toLowerCase().substring(0, 2);
    if (locale === 'ko') return 'korean';
    if (locale === 'ja') return 'japanese';
    if (locale === 'zh') return 'chinese';
    if (locale === 'ar') return 'arabic';
    if (locale === 'ru') return 'cyrillic';
    return 'latin'; // en/de/pt/id and any other Latin-based
}

// Module-scoped cache so repeated calls across tools / pages share fetched bytes.
var _ttfCache = {};

async function fetchTtfAsBase64(url){
    if (_ttfCache[url]) return _ttfCache[url];
    var resp = await fetch(url);
    if (!resp.ok) throw new Error('Font fetch failed: ' + url + ' (' + resp.status + ')');
    var buf = await resp.arrayBuffer();
    // Convert ArrayBuffer → base64 in chunks to avoid stack overflow on large
    // (CJK) fonts. String.fromCharCode.apply on a 2.5 MB buffer blows the
    // argument limit on some browsers; chunked accumulation is safe.
    var bytes = new Uint8Array(buf);
    var binary = '';
    var CHUNK = 0x8000;
    for (var i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
    }
    var b64 = btoa(binary);
    _ttfCache[url] = b64;
    return b64;
}

window.__pdfLoadUnicodeFont = async function(doc, scriptOverride){
    if (!doc || typeof doc.addFileToVFS !== 'function' || typeof doc.addFont !== 'function') {
        throw new Error('__pdfLoadUnicodeFont: argument is not a jsPDF instance');
    }
    if (doc._unicodeFontFamily) {
        // Already loaded on this doc — return cached family name.
        return doc._unicodeFontFamily;
    }
    var script = scriptOverride || localeToScript(window.__LOCALE);
    var set = FONT_SETS[script] || FONT_SETS.latin;
    var family = set.family;

    // Load weights in parallel; jsPDF needs each (filename, family, weight) tuple
    // registered separately so setFont(family, 'bold') resolves the right file.
    var weights = Object.keys(set.files);
    var loaded = await Promise.all(weights.map(function(weight){
        return fetchTtfAsBase64(set.files[weight]).then(function(b64){
            // Use the URL basename as VFS key to avoid collisions when multiple
            // weights share a family. jsPDF treats the VFS as a flat namespace.
            var vfsName = set.files[weight].split('/').pop();
            doc.addFileToVFS(vfsName, b64);
            doc.addFont(vfsName, family, weight);
            return weight;
        });
    }));

    doc._unicodeFontFamily = family;
    doc._unicodeFontWeights = loaded;
    // Make the new family the default. Tools that explicitly setFont('helvetica',...)
    // later will revert — they should be migrated to use the family this helper
    // returns. Intentionally NOT monkey-patching setFont — keeps the contract
    // explicit for migration.
    doc.setFont(family, 'normal');
    return family;
};

// Convenience: call this with a doc to switch to bold safely (falls back to
// 'normal' weight if the script's font set only ships one weight, avoiding
// jsPDF's "font Roboto-bold not found" error on CJK-only docs).
window.__pdfSetFont = function(doc, weight){
    var family = doc._unicodeFontFamily || 'helvetica';
    var weights = doc._unicodeFontWeights || ['normal'];
    var w = weights.indexOf(weight) > -1 ? weight : weights[0];
    // Use the underlying real setFont (bypasses our monkey-patch to avoid
    // recursion) so __pdfSetFont always lands on the Unicode family.
    var realSetFont = doc._origSetFont || doc.setFont;
    realSetFont.call(doc, family, w);
};

// Top-level convenience: load Unicode font AND monkey-patch the doc's
// setFont so legacy `doc.setFont('helvetica', X)` and `doc.setFont('times', X)`
// calls automatically route to the Unicode family. Tools migrate by changing
// `new jsPDF(...)` → `await window.__pdfReady(new jsPDF(...))` and don't need
// to touch any of their existing setFont call sites.
//
// Courier is intentionally NOT redirected — tools use it for ASCII-only
// monospace data (SHA hashes, timestamps). Falling back to courier built-in
// keeps the proper monospace look without bloating the PDF with another
// embedded TTF.
//
// The patch lives on the doc instance only (not on the jsPDF prototype) so
// we don't break callers that rely on the original setFont semantics.
window.__pdfReady = async function(doc, scriptOverride){
    var family = await window.__pdfLoadUnicodeFont(doc, scriptOverride);
    if (doc._setFontPatched) return doc; // idempotent
    doc._origSetFont = doc.setFont.bind(doc);
    doc.setFont = function(font, weight){
        var f = (font || '').toLowerCase();
        if (f === 'helvetica' || f === 'times' || f === family.toLowerCase()) {
            return window.__pdfSetFont(doc, weight);
        }
        // Courier and any other font → original behaviour (built-in PDF font).
        return doc._origSetFont(font, weight);
    };
    doc._setFontPatched = true;
    return doc;
};

})();

// Drain stub queue installed by timbrica-core.js __sharedFunction
// (defer-onerror race defense — see helper comment for context).
if (typeof window.__drainSharedFunction === 'function') window.__drainSharedFunction('__pdfReady');
if (typeof window.__drainSharedFunction === 'function') window.__drainSharedFunction('__pdfLoadUnicodeFont');
